/**
 * InvoicesSection — Admin UI for the Phase AC invoicing system.
 *
 * Mounted in the Financial tab. Renders:
 *   • A filterable list of invoices (status chips, search).
 *   • Create/Edit invoice modal — manual line items + optional
 *     "Import bookings for period" helper.
 *   • Row actions: Download PDF, Issue, Mark Paid, Void.
 *
 * Backend endpoints:
 *   GET  /api/admin/invoices?status=&search=
 *   POST /api/admin/invoices
 *   GET  /api/admin/invoices/{id}
 *   PUT  /api/admin/invoices/{id}
 *   POST /api/admin/invoices/{id}/issue
 *   POST /api/admin/invoices/{id}/mark-paid
 *   POST /api/admin/invoices/{id}/void
 *   POST /api/admin/invoices/preview-bookings
 *   POST /api/admin/invoices/{id}/import-bookings
 *   GET  /api/admin/invoices/{id}/pdf
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

const STATUS_LABEL = {
  draft:  { text: 'Draft',   bg: 'hsl(220 15% 92%)', fg: 'hsl(220 15% 35%)' },
  issued: { text: 'Issued',  bg: 'hsl(45 95% 92%)',  fg: 'hsl(35 80% 30%)'  },
  paid:   { text: 'Paid',    bg: 'hsl(140 60% 92%)', fg: 'hsl(140 45% 26%)' },
  void:   { text: 'Void',    bg: 'hsl(0 0% 92%)',    fg: 'hsl(0 0% 40%)'    },
};

const fmtMoney = (v, currency = 'GBP') => {
  const n = Number(v || 0);
  return (currency === 'GBP' ? '£' : '') + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-GB'); } catch { return String(v).slice(0, 10); }
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const isoPlus = (days) => {
  const d = new Date(); d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
};

export function InvoicesSection({ parents = [] }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState(''); // '' | draft | issued | paid | void
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // invoice doc

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listInvoices({ status: statusFilter || undefined, search: search || undefined });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);
  useEffect(() => { load(); }, [load]);

  const parentsById = useMemo(() => {
    const map = {};
    for (const p of parents || []) map[p._id] = p;
    return map;
  }, [parents]);

  const totals = useMemo(() => {
    let outstanding = 0, paid = 0;
    for (const r of rows) {
      if (r.status === 'paid') paid += Number(r.total || 0);
      else if (r.status === 'issued') outstanding += Number(r.total || 0);
    }
    return { outstanding, paid };
  }, [rows]);

  const downloadPdf = async (invoice) => {
    try {
      const getBlob = api.getInvoicePdfUrl(invoice._id);
      const blob = await getBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoice_number || invoice._id.slice(0, 8)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.message || 'Download failed');
    }
  };
  const openPdf = async (invoice) => {
    try {
      const getBlob = api.getInvoicePdfUrl(invoice._id);
      const blob = await getBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.message || 'Open failed');
    }
  };
  const issue = async (invoice) => {
    try {
      const updated = await api.issueInvoice(invoice._id);
      toast.success(`Invoice issued as ${updated.invoice_number}`);
      load();
    } catch (err) { toast.error(err?.message || 'Failed'); }
  };
  const markPaid = async (invoice) => {
    const ref = window.prompt(`Payment reference for invoice ${invoice.invoice_number || '(draft)'}? (optional)`, '');
    if (ref === null) return; // cancelled
    try {
      const updated = await api.markInvoicePaid(invoice._id, {
        method: 'manual',
        reference: ref || undefined,
        note: 'Marked paid by admin',
      });
      toast.success(`Marked paid — ${updated.invoice_number || 'issued'}`);
      load();
    } catch (err) { toast.error(err?.message || 'Failed'); }
  };
  const voidInv = async (invoice) => {
    if (!window.confirm(`Void invoice ${invoice.invoice_number || '(draft)'}? This cannot be undone.`)) return;
    try {
      await api.voidInvoice(invoice._id);
      toast.success('Invoice voided');
      load();
    } catch (err) { toast.error(err?.message || 'Failed'); }
  };

  // Admin statement download for a parent (uses the invoice row's parent).
  const downloadStatement = async (invoice) => {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
    try {
      const blob = await api.adminGetStatementPdf(invoice.parent_user_id, {
        date_from: first.toISOString(), date_to: last.toISOString(),
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(err?.message || 'Statement failed'); }
  };

  // Force reminder tick (helpful for testing).
  const runReminders = async () => {
    try {
      const r = await api.tickInvoiceReminders();
      const s = r?.stats || {};
      toast.success(
        `Reminders · scanned ${s.scanned || 0} · sent ${(s.sent_upcoming || 0) + (s.sent_overdue || 0)} (${s.sent_upcoming || 0} upcoming, ${s.sent_overdue || 0} overdue)`
      );
    } catch (err) { toast.error(err?.message || 'Failed'); }
  };

  // Credit note modal state.
  const [creditNoteFor, setCreditNoteFor] = useState(null);

  return (
    <div className="card" data-testid="invoices-card" style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0 }}>🧾 Invoices</h3>
          <p style={{ margin: '0.15rem 0 0', color: 'hsl(var(--muted-foreground))', fontSize: '0.82rem' }}>
            Create, download and reconcile per-parent invoices. Outstanding <strong>{fmtMoney(totals.outstanding)}</strong> · Paid <strong>{fmtMoney(totals.paid)}</strong>.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={runReminders}
          data-testid="invoices-reminders-btn"
          style={{ fontSize: '0.82rem' }}
          title="Send due-date reminders now for every unpaid invoice"
        >
          🔔 Run reminders
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setCreating(true)}
          data-testid="invoices-new-btn"
        >
          ➕ New invoice
        </button>
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {[['', 'All'], ['draft', 'Draft'], ['issued', 'Issued'], ['paid', 'Paid'], ['void', 'Void']].map(([k, label]) => (
          <button
            key={k || 'all'}
            type="button"
            onClick={() => setStatusFilter(k)}
            data-testid={`invoices-filter-${k || 'all'}`}
            className={`sub-tab ${statusFilter === k ? 'active' : ''}`}
            style={{
              padding: '0.3rem 0.7rem', fontSize: '0.78rem',
              background: statusFilter === k ? 'hsl(var(--primary))' : 'transparent',
              color: statusFilter === k ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
              border: statusFilter === k ? 'none' : '1px solid hsl(var(--border))',
              borderRadius: 999, cursor: 'pointer', fontWeight: 600,
            }}
          >{label}</button>
        ))}
        <input
          type="search"
          placeholder="Search invoice number or notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
          data-testid="invoices-search"
          style={{ flex: 1, minWidth: 180, padding: '0.35rem 0.6rem', border: '1px solid hsl(var(--border))', borderRadius: 6, fontSize: '0.82rem' }}
        />
        <button type="button" className="btn btn-ghost" onClick={load} data-testid="invoices-refresh" style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem' }}>🔄 Refresh</button>
      </div>

      {loading ? (
        <div className="loading" data-testid="invoices-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state" data-testid="invoices-empty">
          No invoices yet. Click <strong>New invoice</strong> to create your first one.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }} data-testid="invoices-table">
            <thead>
              <tr style={{ borderBottom: '2px solid hsl(var(--border))', textAlign: 'left' }}>
                <th style={{ padding: '0.4rem 0.5rem' }}>Number</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Parent</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Issue date</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Due date</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Status</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = STATUS_LABEL[r.status] || STATUS_LABEL.draft;
                return (
                  <tr key={r._id} data-testid={`invoice-row-${r._id}`} style={{ borderTop: '1px solid hsl(var(--border))' }}>
                    <td style={{ padding: '0.5rem' }}>
                      <button type="button" className="link-btn" onClick={() => setEditing(r)} style={{ background: 'transparent', border: 'none', color: 'hsl(var(--primary))', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                        {r.invoice_number || `DRAFT · ${r._id.slice(0, 8)}`}
                      </button>
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      {r.parent_name || parentsById[r.parent_user_id]?.full_name || r.parent_user_id?.slice(0, 8)}
                      {r.parent_email && (
                        <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>{r.parent_email}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem' }}>{fmtDate(r.issue_date)}</td>
                    <td style={{ padding: '0.5rem' }}>{fmtDate(r.due_date)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoney(r.total, r.currency)}</td>
                    <td style={{ padding: '0.5rem' }}>
                      <span data-testid={`invoice-status-${r._id}`} style={{ display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: 999, background: s.bg, color: s.fg, fontSize: '0.72rem', fontWeight: 700 }}>{s.text}</span>
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" onClick={() => openPdf(r)} title="Open PDF in new tab" className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }} data-testid={`invoice-view-${r._id}`}>📄 View</button>
                      {' '}
                      <button type="button" onClick={() => downloadPdf(r)} title="Download PDF" className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }} data-testid={`invoice-download-${r._id}`}>⬇</button>
                      {' '}
                      {r.status === 'draft' && (
                        <button type="button" onClick={() => issue(r)} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }} data-testid={`invoice-issue-${r._id}`}>🚀 Issue</button>
                      )}
                      {r.status !== 'paid' && r.status !== 'void' && (
                        <button type="button" onClick={() => markPaid(r)} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', color: 'hsl(140 45% 30%)' }} data-testid={`invoice-mark-paid-${r._id}`}>✓ Mark paid</button>
                      )}
                      {r.status !== 'void' && (
                        <button type="button" onClick={() => voidInv(r)} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', color: 'hsl(0 60% 40%)' }} data-testid={`invoice-void-${r._id}`}>🗑</button>
                      )}
                      {(r.status === 'issued' || r.status === 'paid') && (
                        <>
                          <button type="button" onClick={() => setCreditNoteFor(r)} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', color: 'hsl(30 80% 30%)' }} data-testid={`invoice-credit-${r._id}`} title="Issue credit note">↩ Credit</button>
                          <button type="button" onClick={() => downloadStatement(r)} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }} data-testid={`invoice-statement-${r._id}`} title="This month's statement for this parent">📊</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <InvoiceEditor
          parents={parents}
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
      {creditNoteFor && (
        <CreditNoteModal
          invoice={creditNoteFor}
          onClose={() => setCreditNoteFor(null)}
          onSaved={() => { setCreditNoteFor(null); toast.success('Credit note issued'); load(); }}
        />
      )}
    </div>
  );
}

function CreditNoteModal({ invoice, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState([]);
  const [form, setForm] = useState({
    reason: '',
    notes: '',
    line_items: [{ description: '', qty: 1, rate: 0 }],
  });

  useEffect(() => {
    (async () => {
      try {
        const rows = await api.listCreditNotesForInvoice(invoice._id);
        setExisting(Array.isArray(rows) ? rows : []);
      } catch { /* ignore */ }
    })();
  }, [invoice._id]);

  const updateLine = (i, patch) => {
    const next = [...form.line_items];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, line_items: next });
  };
  const total = useMemo(() => (form.line_items || []).reduce((n, li) => n + Number(li.qty || 0) * Number(li.rate || 0), 0), [form.line_items]);

  const openPdf = async (cn) => {
    try {
      const blob = await api.getCreditNotePdf(cn._id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(err?.message || 'Failed'); }
  };

  const save = async () => {
    if (!form.reason.trim()) { toast.error('Reason is required'); return; }
    const cleaned = form.line_items.filter((li) => (li.description || '').trim() && Number(li.rate) > 0);
    if (cleaned.length === 0) { toast.error('At least one line item required'); return; }
    setSaving(true);
    try {
      await api.createCreditNote(invoice._id, { reason: form.reason, notes: form.notes, line_items: cleaned });
      onSaved && onSaved();
    } catch (err) { toast.error(err?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => (saving ? null : onClose())} data-testid="credit-note-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>↩ Issue credit note</h2>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving} data-testid="credit-note-close">✕</button>
        </div>
        <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
          Against invoice <strong>{invoice.invoice_number}</strong> · {invoice.parent_name || invoice.parent_user_id?.slice(0, 8)}
        </p>

        {existing.length > 0 && (
          <div style={{ marginBottom: '0.8rem', padding: '0.55rem 0.75rem', background: 'hsl(35 95% 96%)', border: '1px solid hsl(35 85% 82%)', borderRadius: 8, fontSize: '0.82rem' }}>
            <strong>Existing credit notes on this invoice:</strong>
            <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.1rem' }}>
              {existing.map((cn) => (
                <li key={cn._id}>
                  {cn.credit_note_number} · {fmtMoney(cn.total, cn.currency)} · <em>{cn.reason}</em>
                  {' '}
                  <button type="button" onClick={() => openPdf(cn)} className="link-btn" style={{ background: 'transparent', border: 'none', color: 'hsl(var(--primary))', cursor: 'pointer', padding: 0, fontSize: '0.82rem' }}>📄 PDF</button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="form-group"><label>Reason *</label>
          <input type="text" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Goodwill credit for closure day" data-testid="credit-reason" maxLength={200} />
        </div>

        <div style={{ border: '1px solid hsl(var(--border))', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead><tr style={{ background: 'hsl(var(--muted) / 0.4)' }}>
              <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Description</th>
              <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', width: 60 }}>Qty</th>
              <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', width: 90 }}>Rate</th>
              <th style={{ width: 30 }} />
            </tr></thead>
            <tbody>
              {form.line_items.map((li, i) => (
                <tr key={i} style={{ borderTop: '1px solid hsl(var(--border))' }}>
                  <td style={{ padding: '0.3rem' }}><input type="text" value={li.description} onChange={(e) => updateLine(i, { description: e.target.value })} style={{ width: '100%', padding: '0.25rem 0.4rem' }} data-testid={`credit-line-desc-${i}`} /></td>
                  <td style={{ padding: '0.3rem' }}><input type="number" min="0" step="0.5" value={li.qty} onChange={(e) => updateLine(i, { qty: e.target.value })} style={{ width: '100%', padding: '0.25rem 0.4rem', textAlign: 'right' }} data-testid={`credit-line-qty-${i}`} /></td>
                  <td style={{ padding: '0.3rem' }}><input type="number" min="0" step="0.01" value={li.rate} onChange={(e) => updateLine(i, { rate: e.target.value })} style={{ width: '100%', padding: '0.25rem 0.4rem', textAlign: 'right' }} data-testid={`credit-line-rate-${i}`} /></td>
                  <td><button type="button" onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, x) => x !== i) })} className="btn btn-ghost" style={{ fontSize: '0.75rem', color: 'hsl(0 60% 40%)' }}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '0.4rem', textAlign: 'right' }}>
            <button type="button" className="btn btn-ghost" onClick={() => setForm({ ...form, line_items: [...form.line_items, { description: '', qty: 1, rate: 0 }] })} data-testid="credit-line-add" style={{ fontSize: '0.8rem' }}>➕ Add line</button>
          </div>
        </div>

        <div className="form-group" style={{ marginTop: '0.5rem' }}><label>Notes (optional)</label>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="credit-notes" style={{ width: '100%', padding: '0.4rem 0.5rem', fontFamily: 'inherit' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
          <div style={{ fontSize: '0.9rem' }}>Total credit: <strong style={{ color: 'hsl(30 80% 30%)' }}>{fmtMoney(total, invoice.currency)}</strong></div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving} data-testid="credit-cancel">Cancel</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving} data-testid="credit-save">{saving ? 'Saving…' : '💾 Issue credit note'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InvoiceEditor({ parents = [], initial, onClose, onSaved }) {
  const isNew = !initial;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    parent_user_id: initial?.parent_user_id || '',
    issue_date: initial?.issue_date?.slice(0, 10) || todayIso(),
    due_date: initial?.due_date?.slice(0, 10) || isoPlus(7),
    period_start: initial?.period_start?.slice(0, 10) || '',
    period_end: initial?.period_end?.slice(0, 10) || '',
    terms_label: initial?.terms_label || 'Monthly',
    currency: initial?.currency || 'GBP',
    discount_amount: initial?.discount_amount || 0,
    notes: initial?.notes || '',
    line_items: initial?.line_items || [
      { description: '', qty: 1, rate: 0, amount: 0 },
    ],
  }));

  const totals = useMemo(() => {
    const sub = (form.line_items || []).reduce((n, li) => n + Number(li.qty || 0) * Number(li.rate || 0), 0);
    const total = Math.max(0, sub - Number(form.discount_amount || 0));
    return { subtotal: sub, total };
  }, [form.line_items, form.discount_amount]);

  const updateLine = (i, patch) => {
    const next = [...(form.line_items || [])];
    next[i] = { ...next[i], ...patch };
    if ('qty' in patch || 'rate' in patch) {
      next[i].amount = Number(next[i].qty || 0) * Number(next[i].rate || 0);
    }
    setForm({ ...form, line_items: next });
  };
  const addLine = () => setForm({ ...form, line_items: [...(form.line_items || []), { description: '', qty: 1, rate: 0, amount: 0 }] });
  const removeLine = (i) => setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) });

  const importBookings = async () => {
    if (!form.parent_user_id) { toast.error('Choose a parent first'); return; }
    if (!form.period_start || !form.period_end) { toast.error('Enter period start & end first'); return; }
    try {
      const res = await api.previewInvoiceBookings({
        parent_user_id: form.parent_user_id,
        period_start: `${form.period_start}T00:00:00`,
        period_end: `${form.period_end}T23:59:59`,
      });
      const proposed = res?.lines || [];
      if (proposed.length === 0) { toast.info('No confirmed bookings found for this parent in this period'); return; }
      // De-dup on booking_id vs current lines
      const existing = new Set((form.line_items || []).map((li) => (li.meta || {}).booking_id).filter(Boolean));
      const filtered = proposed.filter((l) => !existing.has((l.meta || {}).booking_id));
      if (filtered.length === 0) { toast.info('All those bookings are already on this invoice'); return; }
      setForm({ ...form, line_items: [...(form.line_items || []).filter((li) => li.description || li.rate), ...filtered] });
      toast.success(`Added ${filtered.length} line${filtered.length === 1 ? '' : 's'} from bookings`);
    } catch (err) { toast.error(err?.message || 'Import failed'); }
  };

  const save = async (issueImmediately = false) => {
    if (!form.parent_user_id) { toast.error('Choose a parent'); return; }
    const cleanedLines = (form.line_items || []).filter((li) => (li.description || '').trim() || Number(li.rate) > 0);
    if (cleanedLines.length === 0) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    try {
      const payload = {
        parent_user_id: form.parent_user_id,
        issue_date: form.issue_date ? `${form.issue_date}T00:00:00` : undefined,
        due_date: form.due_date ? `${form.due_date}T00:00:00` : undefined,
        period_start: form.period_start ? `${form.period_start}T00:00:00` : undefined,
        period_end: form.period_end ? `${form.period_end}T23:59:59` : undefined,
        terms_label: form.terms_label,
        currency: form.currency,
        discount_amount: Number(form.discount_amount || 0),
        notes: form.notes || '',
        line_items: cleanedLines,
        status: issueImmediately ? 'issued' : 'draft',
      };
      let saved;
      if (isNew) {
        saved = await api.createInvoice(payload);
      } else {
        saved = await api.updateInvoice(initial._id, payload);
      }
      if (!isNew && issueImmediately && saved.status === 'draft') {
        await api.issueInvoice(saved._id);
      }
      toast.success(isNew
        ? (issueImmediately ? `Invoice created & issued` : 'Draft invoice created')
        : 'Invoice updated');
      onSaved && onSaved(saved);
    } catch (err) {
      toast.error(err?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => (saving ? null : onClose())} data-testid="invoice-editor-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>{isNew ? '➕ New invoice' : `✏️ Edit ${initial?.invoice_number || 'draft invoice'}`}</h2>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving} data-testid="invoice-editor-close">✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.85rem' }}>
          <div className="form-group">
            <label>Parent *</label>
            <select
              value={form.parent_user_id}
              onChange={(e) => setForm({ ...form, parent_user_id: e.target.value })}
              data-testid="invoice-parent-select"
              disabled={!isNew}
            >
              <option value="">— select parent —</option>
              {(parents || []).map((p) => (
                <option key={p._id} value={p._id}>{p.full_name} · {p.email}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Terms</label>
            <select
              value={form.terms_label}
              onChange={(e) => setForm({ ...form, terms_label: e.target.value })}
              data-testid="invoice-terms"
            >
              {['Weekly', 'Fortnightly', 'Every 4 weeks', 'Monthly', 'On receipt', 'Net 7', 'Net 14', 'Net 30'].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Issue date</label>
            <input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="invoice-issue-date" />
          </div>
          <div className="form-group">
            <label>Due date</label>
            <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due-date" />
          </div>
          <div className="form-group">
            <label>Period start</label>
            <input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="invoice-period-start" />
          </div>
          <div className="form-group">
            <label>Period end</label>
            <input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="invoice-period-end" />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0' }}>
          <button type="button" className="btn btn-secondary" onClick={importBookings} data-testid="invoice-import-bookings" style={{ fontSize: '0.82rem', padding: '0.35rem 0.7rem' }}>
            📥 Import bookings for period
          </button>
          <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
            Adds one line per confirmed booking in the period. Duplicates are skipped.
          </span>
        </div>

        <div style={{ border: '1px solid hsl(var(--border))', borderRadius: 8, marginTop: '0.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }} data-testid="invoice-lines-table">
            <thead>
              <tr style={{ background: 'hsl(var(--muted) / 0.4)' }}>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', width: 60 }}>Qty</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', width: 90 }}>Rate</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', width: 100 }}>Amount</th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {(form.line_items || []).map((li, i) => (
                <tr key={i} data-testid={`invoice-line-${i}`} style={{ borderTop: '1px solid hsl(var(--border))' }}>
                  <td style={{ padding: '0.3rem' }}>
                    <input type="text" value={li.description || ''} onChange={(e) => updateLine(i, { description: e.target.value })} style={{ width: '100%', padding: '0.25rem 0.4rem' }} data-testid={`invoice-line-desc-${i}`} />
                  </td>
                  <td style={{ padding: '0.3rem' }}>
                    <input type="number" step="0.5" min="0" value={li.qty ?? 1} onChange={(e) => updateLine(i, { qty: e.target.value })} style={{ width: '100%', padding: '0.25rem 0.4rem', textAlign: 'right' }} data-testid={`invoice-line-qty-${i}`} />
                  </td>
                  <td style={{ padding: '0.3rem' }}>
                    <input type="number" step="0.01" min="0" value={li.rate ?? 0} onChange={(e) => updateLine(i, { rate: e.target.value })} style={{ width: '100%', padding: '0.25rem 0.4rem', textAlign: 'right' }} data-testid={`invoice-line-rate-${i}`} />
                  </td>
                  <td style={{ padding: '0.3rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoney(li.amount, form.currency)}</td>
                  <td>
                    <button type="button" onClick={() => removeLine(i)} className="btn btn-ghost" style={{ fontSize: '0.75rem', color: 'hsl(0 60% 40%)', padding: '0.15rem 0.4rem' }} data-testid={`invoice-line-remove-${i}`}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '0.4rem', textAlign: 'right' }}>
            <button type="button" className="btn btn-ghost" onClick={addLine} data-testid="invoice-line-add" style={{ fontSize: '0.8rem' }}>➕ Add line</button>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '0.6rem', alignItems: 'center' }}>
          <label style={{ fontSize: '0.85rem' }}>Discount
            <input type="number" step="0.01" min="0" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} data-testid="invoice-discount" style={{ width: 100, marginLeft: 8, padding: '0.25rem 0.4rem', textAlign: 'right' }} />
          </label>
          <div style={{ fontSize: '0.95rem' }}>
            Subtotal <strong>{fmtMoney(totals.subtotal, form.currency)}</strong> · Total <strong style={{ color: 'hsl(var(--primary))' }}>{fmtMoney(totals.total, form.currency)}</strong>
          </div>
        </div>

        <div className="form-group" style={{ marginTop: '0.75rem' }}>
          <label>Notes / activity list (shown on PDF)</label>
          <textarea
            rows={4}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g. 1. School Pick up  2. Play time  3. Mandatory meal …  Period 14/08 – 13/09"
            data-testid="invoice-notes"
            style={{ width: '100%', fontFamily: 'inherit', padding: '0.4rem 0.5rem' }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving} data-testid="invoice-editor-cancel">Cancel</button>
          {isNew && (
            <button type="button" className="btn btn-secondary" onClick={() => save(false)} disabled={saving} data-testid="invoice-editor-save-draft">
              {saving ? 'Saving…' : '💾 Save draft'}
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => save(true)} disabled={saving} data-testid="invoice-editor-save-issue">
            {saving ? 'Saving…' : (isNew ? '🚀 Save & issue' : '💾 Save changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InvoicesSection;
